import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type ThemeType = 'glass-morphism' | 'soft-shadows' | 'corporate'

export interface StarForgeSettings {
  theme: ThemeType
  notificationSound: string
}

interface ThemeContextType {
  theme: ThemeType
  setTheme: (theme: ThemeType) => void
  settings: StarForgeSettings
  updateSettings: (settings: Partial<StarForgeSettings>) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'starforge-settings'

const DEFAULT_SETTINGS: StarForgeSettings = {
  theme: 'glass-morphism',
  notificationSound: 'chime'
}

function loadSettings(): StarForgeSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return { ...DEFAULT_SETTINGS, ...parsed }
    }
  } catch (error) {
    console.error('Failed to load settings:', error)
  }
  return DEFAULT_SETTINGS
}

function saveSettings(settings: StarForgeSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to save settings:', error)
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<StarForgeSettings>(loadSettings)

  // Apply theme class to document element
  useEffect(() => {
    // Remove all theme classes
    document.documentElement.classList.remove('theme-glass-morphism', 'theme-soft-shadows', 'theme-corporate')
    // Add current theme class
    document.documentElement.classList.add(`theme-${settings.theme}`)
  }, [settings.theme])

  // Save settings whenever they change
  useEffect(() => {
    saveSettings(settings)
  }, [settings])

  const setTheme = (theme: ThemeType) => {
    setSettings(prev => ({ ...prev, theme }))
  }

  const updateSettings = (newSettings: Partial<StarForgeSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }))
  }

  return (
    <ThemeContext.Provider value={{ theme: settings.theme, setTheme, settings, updateSettings }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
